<script setup>
// "Converter em editável": o modelo HTML original × a versão do editor visual
// (iframes sem scripts, como a galeria) e o resumo da conversão. "Criar cópia
// editável" cria um modelo novo (o original fica) e abre no editor visual.
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import API from 'dashboard/api/marketingEmailTemplates';

const props = defineProps({
  templateId: { type: [String, Number], required: true },
  originalHtml: { type: String, default: '' },
  beforeConvert: { type: Function, default: null },
});
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const dialog = ref(null);
const loading = ref(false);
const creating = ref(false);
const error = ref('');
const previewHtml = ref('');
const stats = ref(null);
let seq = 0;

const failure = e =>
  e?.response?.data?.error || t('EMAIL_TEMPLATES.EDITOR.CONVERT.FAILED');

const open = async () => {
  seq += 1;
  const my = seq;
  error.value = '';
  previewHtml.value = '';
  stats.value = null;
  dialog.value?.open();
  loading.value = true;
  try {
    await props.beforeConvert?.();
    const { data } = await API.convertToLayout(props.templateId);
    if (my !== seq) return;
    previewHtml.value = data.preview_html || '';
    stats.value = data.stats || null;
  } catch (e) {
    if (my === seq) error.value = failure(e);
  } finally {
    if (my === seq) loading.value = false;
  }
};

const create = async () => {
  if (creating.value || !stats.value) return;
  creating.value = true;
  try {
    const { data } = await API.convertToLayout(props.templateId, {
      create: true,
    });
    dialog.value?.close();
    router.push({
      name: 'email_templates_builder',
      params: { accountId: route.params.accountId, templateId: data.id },
    });
  } catch (e) {
    error.value = failure(e);
  } finally {
    creating.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialog"
    :show-confirm-button="false"
    :show-cancel-button="false"
    full-screen
    overflow-y-auto
  >
    <div class="flex flex-wrap items-center gap-3">
      <button
        type="button"
        data-test="close"
        class="flex size-8 items-center justify-center rounded-lg text-n-slate-11 hover:bg-n-solid-3"
        :aria-label="t('EMAIL_TEMPLATES.EDITOR.CONVERT.CLOSE')"
        @click="dialog?.close()"
      >
        <span class="i-lucide-x" />
      </button>
      <div class="min-w-[12rem] flex-1">
        <p class="text-sm font-semibold text-n-slate-12">
          {{ t('EMAIL_TEMPLATES.EDITOR.CONVERT.TITLE') }}
        </p>
        <p class="text-xs text-n-slate-11">
          {{ t('EMAIL_TEMPLATES.EDITOR.CONVERT.DESCRIPTION') }}
        </p>
      </div>
      <p v-if="stats" data-test="summary" class="text-xs text-n-slate-11">
        {{
          t('EMAIL_TEMPLATES.EDITOR.CONVERT.SUMMARY', {
            elements: stats.elements,
            html: stats.html_blocks,
          })
        }}
      </p>
      <button
        type="button"
        data-test="create"
        :disabled="loading || creating || !stats"
        class="shrink-0 rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        @click="create"
      >
        {{ t('EMAIL_TEMPLATES.EDITOR.CONVERT.CREATE') }}
      </button>
    </div>
    <p
      v-if="error"
      data-test="error"
      class="rounded-lg border border-n-ruby-6 bg-n-ruby-2 px-3 py-2 text-sm text-n-ruby-11"
    >
      {{ error }}
    </p>
    <div class="grid min-h-[70vh] flex-1 grid-cols-1 gap-4 md:grid-cols-2">
      <section class="flex min-h-0 flex-col gap-2">
        <span class="text-xs font-semibold text-n-slate-11">
          {{ t('EMAIL_TEMPLATES.EDITOR.CONVERT.ORIGINAL') }}
        </span>
        <iframe
          sandbox=""
          class="min-h-[60vh] w-full flex-1 rounded-xl border border-n-weak bg-white"
          :title="t('EMAIL_TEMPLATES.EDITOR.CONVERT.ORIGINAL')"
          :srcdoc="originalHtml"
        />
      </section>
      <section class="flex min-h-0 flex-col gap-2">
        <span class="text-xs font-semibold text-n-slate-11">
          {{ t('EMAIL_TEMPLATES.EDITOR.CONVERT.EDITABLE') }}
        </span>
        <p
          v-if="loading"
          class="flex min-h-[60vh] flex-1 items-center justify-center rounded-xl border border-n-weak text-sm text-n-slate-11"
        >
          {{ t('EMAIL_TEMPLATES.EDITOR.CONVERT.LOADING') }}
        </p>
        <iframe
          v-else
          sandbox=""
          class="min-h-[60vh] w-full flex-1 rounded-xl border border-n-weak bg-white"
          :title="t('EMAIL_TEMPLATES.EDITOR.CONVERT.EDITABLE')"
          :srcdoc="previewHtml"
        />
      </section>
    </div>
  </Dialog>
</template>
